/**
 * TrainingCard (TRNG004) create/update result classification.
 * Reuses postRequest __httpStatus via getServiceTaskHttpStatus. Does not reuse ServiceTask message keys.
 * HTTP 4xx/5xx is never treated as offline. There is no offline queue or automatic retry.
 */
import { firstValidationDetail, getServiceTaskHttpStatus } from './serviceTaskCreateHttp';
import { normalizeMachineIds, normalizePositiveId } from './trainingSelectionCascade';

export const TRAINING_CARD_SAVE_PATH = '/api/training_cards';
export const TRAINING_CARD_UPDATE_PATH_PREFIX = '/api/training_cards/update/';

export const TRAINING_CARD_SAVE_MESSAGES = Object.freeze({
  success: 'TrainingCard.Save.RecordSaved',
  validation: 'TrainingCard.Save.ValidationFailed',
  permission: 'TrainingCard.Save.PermissionDenied',
  server: 'TrainingCard.Save.ActionFailed',
  network: 'TrainingCard.Save.NotSaved',
  offline: 'TrainingCard.Save.OfflineNotSaved',
});

export function isActiveTrainingType(training) {
  const v = training?.validity;
  return !(v === 0 || v === '0');
}

export function filterTrng004TrainingOptions(list, {
  departmentId = '',
  ownedIds = [],
  includeTrainingId = null,
} = {}) {
  const deptRaw = departmentId == null || departmentId === '' || (Array.isArray(departmentId) && departmentId.length === 0)
    ? null
    : Number(departmentId);
  const owned = new Set((ownedIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id)));
  return (list || []).filter((training) => {
    const id = Number(training?.id);
    const keepAssigned = includeTrainingId != null && Number(includeTrainingId) === id;
    if (!keepAssigned && owned.has(id)) return false;
    if (!keepAssigned && !isActiveTrainingType(training)) return false;
    if (deptRaw == null || Number.isNaN(deptRaw)) return true;
    const tDept = training?.department_id != null ? Number(training.department_id) : Number(training?.department?.id);
    return tDept === deptRaw;
  });
}

function optionalId(value) {
  return normalizePositiveId(value);
}

export function buildTrainingCardCreatePayload(values, employeeId) {
  return {
    employee_id: optionalId(employeeId),
    training_id: optionalId(values?.training_id),
    initial_date: values?.initial_date ? String(values.initial_date) : null,
    description: values?.description != null && String(values.description) !== '' ? String(values.description) : null,
    company_id: optionalId(values?.company_id),
    machines: normalizeMachineIds(values?.machines),
    lecturer_id: optionalId(values?.lecturer_id),
    company_contact_id: optionalId(values?.company_contact_id),
  };
}

export function buildTrainingCardUpdatePayload(values, employeeId) {
  return {
    ...buildTrainingCardCreatePayload(values, employeeId),
    deactivation_date: values?.deactivation_date ? String(values.deactivation_date) : null,
  };
}

export function laravelErrorsToFormik(errors) {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return {};
  const out = {};
  Object.entries(errors).forEach(([key, messages]) => {
    if (key === 'employee_id') return;
    const text = Array.isArray(messages) ? messages[0] : messages;
    if (text != null && String(text) !== '') out[key] = String(text);
  });
  return out;
}

function hasSavedCardData(result) {
  return result?.data != null && typeof result.data === 'object' && !Array.isArray(result.data);
}

export function interpretTrainingCardSaveResponse(result) {
  if (result == null || typeof result !== 'object' || Array.isArray(result)) {
    return {
      ok: false,
      kind: 'invalid',
      messageKey: TRAINING_CARD_SAVE_MESSAGES.server,
      detail: null,
    };
  }

  const httpStatus = getServiceTaskHttpStatus(result);
  const bodyStatus = typeof result.status === 'number' ? result.status : null;
  const hasErrors = Boolean(result.errors && typeof result.errors === 'object' && !Array.isArray(result.errors));
  const successBody = bodyStatus === 201 || bodyStatus === 200;
  const successHttp = httpStatus == null || (httpStatus >= 200 && httpStatus < 300);

  if (successHttp && successBody && hasSavedCardData(result)) {
    return {
      ok: true,
      kind: 'success',
      httpStatus: httpStatus ?? bodyStatus,
      messageKey: TRAINING_CARD_SAVE_MESSAGES.success,
    };
  }

  const status = httpStatus ?? bodyStatus;
  if (status === 422 || hasErrors) {
    return {
      ok: false,
      kind: 'validation',
      httpStatus: status ?? 422,
      messageKey: TRAINING_CARD_SAVE_MESSAGES.validation,
      detail: firstValidationDetail(result.errors, null),
      fieldErrors: hasErrors ? result.errors : {},
    };
  }
  if (status === 403) {
    return { ok: false, kind: 'permission', httpStatus: 403, messageKey: TRAINING_CARD_SAVE_MESSAGES.permission };
  }
  if (status === 401 || status === 419) {
    return { ok: false, kind: 'auth', httpStatus: status, messageKey: TRAINING_CARD_SAVE_MESSAGES.server };
  }
  if (status === 429 || (status != null && status >= 500) || bodyStatus === 400) {
    return { ok: false, kind: 'server', httpStatus: status, messageKey: TRAINING_CARD_SAVE_MESSAGES.server };
  }
  return { ok: false, kind: 'server', httpStatus: status, messageKey: TRAINING_CARD_SAVE_MESSAGES.server };
}

export function interpretTrainingCardSaveTransportError(isOnline) {
  if (isOnline === false) {
    return { ok: false, kind: 'offline', messageKey: TRAINING_CARD_SAVE_MESSAGES.offline };
  }
  return { ok: false, kind: 'network', messageKey: TRAINING_CARD_SAVE_MESSAGES.network };
}
